import { Card, Grid, Icon, InputLabel, Menu, MenuItem } from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "components/Tables/DataTable";
import dataResiduosTransportados from "./dataResiduosTransportados";
import dataResiduosGenerados from "./dataResiduosGenerados";
import SoftBox from "components/SoftBox";
import { getColor } from "utils/colors";
import SinDatos from "components/Carteles/SinDatos";
import ModalResiduos from "./ModalResiduos";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import Separador from "../Separador";

const ResiduosTransportados = ({
  idDeclaracion,
  listadoResiduosTransportados,
  setListadoResiduosTransportados,
  finalizada,
  generados,
}) => {
  const [residuo, setResiduo] = useState({});
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedResiduo, setSelectedResiduo] = useState(null);
  const [modalResiduos, setModalResiduos] = useState(false);

  const { columns, getRows } = dataResiduosTransportados;
  const { columnsGenerados, getRowsGenerados } = dataResiduosGenerados;
  const asNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedResiduo(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
    setSelectedResiduo(null);
  }, []);

  const handleChangeResiduo = (e) => {
    const { name, value } = e.target;
    setResiduo((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddResiduo = () => {
    const { idsTratamiento, ...newResiduo } = residuo;
    setListadoResiduosTransportados((prev) => [...prev, newResiduo]);
    setResiduo({});
  };

  const memoizedRows = useMemo(() => {
    if (listadoResiduosTransportados) {
      const listadoSinBorrar = listadoResiduosTransportados.filter((item) => !item.aBorrar);
      return getRows(listadoSinBorrar, handleOpenMenu);
    }
    return [];
  }, [listadoResiduosTransportados, handleOpenMenu]);

  const memoizedRowsGenerados = useMemo(() => {
    if (generados) {
      const listadoSinBorrar = generados.filter((item) => !item.aBorrar);
      return getRowsGenerados(listadoSinBorrar, handleOpenMenu);
    }
    return [];
  }, [generados, handleOpenMenu]);

  // Render del menú contextual
  const renderMenu = !finalizada && (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          setListadoResiduosTransportados((prev) => {
            if (selectedResiduo.id) {
              return prev.map((item) => {
                if (item.id === selectedResiduo.id) {
                  return { ...item, aBorrar: true };
                }
                return item;
              });
            } else {
              return prev.filter((item) => item !== selectedResiduo);
            }
          });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Quitar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            delete
          </Icon>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  return (
    <Grid container p={2}>
      <Grid item xs={12}>
        <SoftTypography variant="h4" fontWeight="bold">
          Residuos Transportados
        </SoftTypography>
      </Grid>

      {!finalizada && (
        <Grid item xs={12}>
          <Grid container p={2} spacing={1}>
            <Grid item xs={12}>
              <SoftTypography variant="h6" fontWeight="bold">
                Residuos cargados en Declaraciones a Transportar
              </SoftTypography>
            </Grid>
            <Grid item xs={12} py={1}>
              {generados ? (
                <DataTable columns={columnsGenerados} rows={memoizedRowsGenerados} />
              ) : (
                <SinDatos textoTitulo="No hay residuos cargados" />
              )}
            </Grid>
            <Grid item xs={12}>
              <SoftTypography variant="h6" fontWeight="bold">
                Nuevo Residuo Transportado
              </SoftTypography>
            </Grid>
            <Grid item xs={2} md={2}>
              <InputLabel htmlFor="tipoResiduo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Elegir tipo de residuo
                </SoftTypography>
              </InputLabel>
              <SoftButton
                variant="gradient"
                color="primary"
                fullWidth
                onClick={() => {
                  setModalResiduos(true);
                }}
              >
                Elegir <Icon>open_in_new</Icon>
              </SoftButton>
            </Grid>
            <Grid item xs={4} md={4}>
              <InputLabel htmlFor="tipoResiduo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Tipo de Residuo elegido
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="tipoResiduo"
                placeholder="Sin elegir"
                value={residuo.tipoResiduo || ""}
                fullWidth
                name="tipoResiduo"
                onChange={handleChangeResiduo}
                disabled
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <InputLabel htmlFor="descripcionResiduo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Descripción del Residuo elegido
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="descripcionResiduo"
                placeholder="Sin elegir"
                value={residuo.descripcionResiduo || ""}
                fullWidth
                name="descripcionResiduo"
                onChange={handleChangeResiduo}
                disabled
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <InputLabel htmlFor="cantidadResiduo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Cantidad
                </SoftTypography>
              </InputLabel>
              <SoftInputNumber
                id="cantidadResiduo"
                placeholder="Cantidad"
                value={asNumberOrNull(residuo.cantidadResiduo)}
                fullWidth
                name="cantidadResiduo"
                onChange={handleChangeResiduo}
              />
            </Grid>
            <Grid item xs={6} md={9}>
              <InputLabel htmlFor="estadoFisico">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Estado físico del residuo
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="estadoFisico"
                placeholder="Estado físico del residuo"
                value={residuo.estadoFisico || ""}
                fullWidth
                name="estadoFisico"
                onChange={handleChangeResiduo}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <InputLabel htmlFor="descripcionTransporteAuditoria">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Descripción de transporte y auditorías
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="descripcionTransporteAuditoria"
                placeholder="Descripción"
                value={residuo.descripcionTransporteAuditoria || ""}
                fullWidth
                name="descripcionTransporteAuditoria"
                onChange={handleChangeResiduo}
              />
            </Grid>
            <Grid item xs={6} md={6} display={"flex"} alignItems="flex-end">
              <SoftButton
                variant="gradient"
                color="primary"
                fullWidth
                onClick={handleAddResiduo}
                disabled={
                  !residuo.tipoResiduo ||
                  !residuo.descripcionResiduo ||
                  !residuo.cantidadResiduo ||
                  !residuo.estadoFisico ||
                  !residuo.descripcionTransporteAuditoria
                }
              >
                Agregar +
              </SoftButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!finalizada && <Separador />}
      <Grid item xs={12} py={1}>
        {listadoResiduosTransportados?.filter((item) => !item.aBorrar).length > 0 ? (
          <DataTable columns={columns} rows={memoizedRows} />
        ) : (
          <SinDatos textoTitulo="No hay residuos cargados" />
        )}
        {renderMenu}
      </Grid>
      <ModalResiduos
        open={modalResiduos}
        handleClose={() => setModalResiduos(false)}
        setResiduo={setResiduo}
        residuo={residuo}
      />
    </Grid>
  );
};

export default memo(ResiduosTransportados);

ResiduosTransportados.propTypes = {
  idDeclaracion: PropTypes.string.isRequired,
  listadoResiduosTransportados: PropTypes.array.isRequired,
  setListadoResiduosTransportados: PropTypes.func.isRequired,
  finalizada: PropTypes.bool,
  generados: PropTypes.array,
};
